import { useMemo, useState } from 'react';
import { Stack, ToggleButtonGroup, ToggleButton, Typography, Popover, IconButton, Box, useMediaQuery, useTheme, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import QueueIcon from '@mui/icons-material/Queue';
import PendingIcon from '@mui/icons-material/Pending';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TodayIcon from '@mui/icons-material/Today';
import EventIcon from '@mui/icons-material/Event';
import CallEndIcon from '@mui/icons-material/CallEnd';
import Paper from '@mui/material/Paper';
import CasesListTable from './cases-list-table';

/**
 * CategorisedCasesList component that displays a toggle button group
 * to switch between queue, pending, pending today, and others modes
 */
const CategorisedCasesList = ({
  onRemoveCaseSelection,
  queueCases,
  pendingCases,
  otherStatusCases,
  pendingTodayCases,
  callDoneButNotDue,
  callDoneCases,
  scheduledLaterCases,
  sameContactCases
}) => {
  // State to track the selected mode - Default to 'queue' or perhaps 'pendingToday' if preferred
  const [mode, setMode] = useState('queue');
  // State for popover
  const [anchorEl, setAnchorEl] = useState(null);
  // Get theme and check if screen is small
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Handle popover open
  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle popover close
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'category-info-popover' : undefined;

  // Handle mode change
  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      console.log('Changing mode to:', newMode); // Log mode change
      setMode(newMode);
    }
  };

  const queueCasesMap = useMemo(() => {
    if (!queueCases || queueCases.length === 0) { // Added null check
      return new Map();
    }
    return new Map(queueCases.map((queueCase) => [queueCase._id, queueCase]));
  }, [queueCases]);

  const pendingCasesMap = useMemo(() => {
    if (!pendingCases || pendingCases.length === 0) { // Added null check
      return new Map();
    }
    return new Map(pendingCases.map((pendingCase) => [pendingCase._id, pendingCase]));
  }, [pendingCases]);

  const pendingTodayCasesMap = useMemo(() => {
    if (!pendingTodayCases || pendingTodayCases.length === 0) { // Added null check
      return new Map();
    }
    return new Map(pendingTodayCases.map((pendingTodayCase) => [pendingTodayCase._id, pendingTodayCase]));
  }, [pendingTodayCases]);

  const otherStatusCasesMap = useMemo(() => {
    if (!otherStatusCases || otherStatusCases.length === 0) { // Added null check
      return new Map();
    }
    return new Map(otherStatusCases.map((otherStatusCase) => [otherStatusCase._id, otherStatusCase]));
  }, [otherStatusCases]);

  const callDoneButNotDueMap = useMemo(() => {
    if (!callDoneButNotDue || callDoneButNotDue.length === 0) { // Added null check
      return new Map();
    }
    return new Map(callDoneButNotDue.map((callDoneButNotDueCase) => [callDoneButNotDueCase._id, callDoneButNotDueCase]));
  }, [callDoneButNotDue]);

  const callDoneCasesMap = useMemo(() => {
    if (!callDoneCases || callDoneCases.length === 0) { // Added null check
      return new Map();
    }
    return new Map(callDoneCases.map((callDoneCase) => [callDoneCase._id, callDoneCase]));
  }, [callDoneCases]);

  const scheduledLaterCasesMap = useMemo(() => {
    if (!scheduledLaterCases || scheduledLaterCases.length === 0) { // Added null check
      return new Map();
    }
    return new Map(scheduledLaterCases.map((scheduledLaterCase) => [scheduledLaterCase._id, scheduledLaterCase]));
  }, [scheduledLaterCases]);

  const sameContactCasesMap = useMemo(() => {
    if (!sameContactCases || sameContactCases.length === 0) { // Added null check
      return new Map();
    }
    return new Map(sameContactCases.map((sameContactCase) => [sameContactCase._id, sameContactCase]));
  }, [sameContactCases]);

  return (
    <Stack spacing={2}>
      <Stack component={Paper} sx={{ p: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
            Cases Category
          </Typography>
          <IconButton aria-describedby={id} size="small" onClick={handlePopoverOpen} sx={{ p: 0 }}> {/* Removed padding */}
            <InfoIcon fontSize="inherit" />
          </IconButton>
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            slotProps={{ // Added slotProps for styling the Paper component
              paper: {
                sx: {
                  boxShadow: 1, // Add subtle shadow
                  borderRadius: 1, // Add rounded corners
                },
              },
            }}
          >
            <Typography sx={{ p: 1.5, maxWidth: 250 }} variant="body2">
              This date becomes stale after assigning. Please click on 'Final Check' again to update it.
              Pending Today cases are those that were assigned today and still in pending status.
              These need to be removed from the allocations tab.
            </Typography>
          </Popover>
        </Stack>
        <Box sx={{
          width: '100%',
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
            borderRadius: '4px',
          },
        }}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={handleModeChange}
            aria-label="case category"
            size="small"
            sx={{
              height: '32px',
              display: 'inline-flex',
              minWidth: isSmallScreen ? 'max-content' : '100%',
              width: isSmallScreen ? 'auto' : '100%',
            }}
          >
            <Tooltip title="Call Done But Not Due" arrow placement="top">
              <ToggleButton value="callDoneButNotDue" aria-label="call done but not due" sx={{ py: 0.5, minWidth: isSmallScreen ? '48px' : 'auto' }}>
                {isSmallScreen ? <CallEndIcon fontSize="small" /> : 'Call Done But Not Due'}
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Pending Today" arrow placement="top">
              <ToggleButton value="pendingToday" aria-label="pending today" sx={{ py: 0.5, minWidth: isSmallScreen ? '48px' : 'auto' }}>
                {isSmallScreen ? <TodayIcon fontSize="small" /> : 'Pending Today'}
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Scheduled Later" arrow placement="top">
              <ToggleButton value="scheduledLater" aria-label="scheduled later" sx={{ py: 0.5, minWidth: isSmallScreen ? '48px' : 'auto' }}>
                {isSmallScreen ? <EventIcon fontSize="small" /> : 'Scheduled Later'}
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Queue" arrow placement="top">
              <ToggleButton value="queue" aria-label="queue" sx={{ py: 0.5, minWidth: isSmallScreen ? '48px' : 'auto' }}>
                {isSmallScreen ? <QueueIcon fontSize="small" /> : 'Queue'}
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Pending" arrow placement="top">
              <ToggleButton value="pending" aria-label="pending" sx={{ py: 0.5, minWidth: isSmallScreen ? '48px' : 'auto' }}>
                {isSmallScreen ? <PendingIcon fontSize="small" /> : 'Pending'}
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Others" arrow placement="top">
              <ToggleButton value="others" aria-label="others" sx={{ py: 0.5, minWidth: isSmallScreen ? '48px' : 'auto' }}>
                {isSmallScreen ? <MoreHorizIcon fontSize="small" /> : 'Others'}
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Call Done" arrow placement="top">
              <ToggleButton value="callDone" aria-label="call done" sx={{ py: 0.5, minWidth: isSmallScreen ? '48px' : 'auto' }}>
                {isSmallScreen ? <CheckCircleOutlineIcon fontSize="small" /> : 'Call Done'}
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Same Contact" arrow placement="top">
              <ToggleButton value="sameContact" aria-label="same contact" sx={{ py: 0.5, minWidth: isSmallScreen ? '48px' : 'auto' }}>
                {isSmallScreen ? <CheckCircleOutlineIcon fontSize="small" /> : 'Same Contact'}
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Box>
      </Stack>

      {/* Content based on selected mode */}
      {mode === 'callDoneButNotDue' && (
        <CasesListTable
          onRemoveCase={onRemoveCaseSelection}
          data={callDoneButNotDueMap}
        />
      )}
      {mode === 'pendingToday' && (
        <>
          <CasesListTable
            onRemoveCase={onRemoveCaseSelection}
            data={pendingTodayCasesMap}
          />
        </>
      )}
      {mode === 'scheduledLater' && (
        <CasesListTable
          onRemoveCase={onRemoveCaseSelection}
          data={scheduledLaterCasesMap}
        />
      )}
      {mode === 'queue' && (
        <CasesListTable
          onRemoveCase={onRemoveCaseSelection}
          data={queueCasesMap}
        />
      )}
      {mode === 'pending' && (
        <CasesListTable
          onRemoveCase={onRemoveCaseSelection}
          data={pendingCasesMap}
        />
      )}
      {mode === 'others' && (
        <CasesListTable
          onRemoveCase={onRemoveCaseSelection}
          data={otherStatusCasesMap}
        />
      )}

      {mode === 'callDone' && (
        <CasesListTable
          onRemoveCase={onRemoveCaseSelection}
          data={callDoneCasesMap}
        />
      )}
      {mode === 'sameContact' && (
        <CasesListTable
          onRemoveCase={onRemoveCaseSelection}
          data={sameContactCasesMap}
        />
      )}
    </Stack>
  );
};

export default CategorisedCasesList;
